import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  normalizePublicSiteLogoValue,
  readPersistedPublicSiteIdentity,
  resolvePublicSiteBrandDisplayName,
  resolvePublicSiteBrandDisplayType,
  resolvePublicSiteBrandLogoUrl,
  resolvePublicSiteHeaderLogo,
} from "./publicSiteIdentity";
import { useOptionalPublicSite } from "./PublicSiteContext";
import { getUserInfo } from "../../services/localStorage";
import { normalizeAgentImageValue } from "../../utils/resolveAgentCompanyLogoUrl";
import { HOTELSRIVER_OFFICIAL_LOGO_URL } from "../../utils/hotelsRiverBrandLogo";
import { appendPublicSiteToken } from "../../utils/publicSiteUrl";

const HEADER_LOGO_TRACE = "PUBLIC_SITE_HEADER_LOGO_TRACE";

/**
 * ברנדינג לרכיבי UI תחת PublicSiteProvider (מיני‑סייט ציבורי).
 * ברירת HotelsRiver במקומות ללא Provider או כשזה עדיין נטען.
 */
export function usePublicSiteBranding() {
  const ctx = useOptionalPublicSite();
  return useMemo(() => {
    const requiresPublicSite = Boolean(ctx?.requiresPublicSite);
    const siteIdentityStatus = ctx?.siteIdentityStatus ?? null;
    const si = ctx?.siteIdentity ?? null;
    const ready = siteIdentityStatus === "ready";
    let brandLogoUrl =
      requiresPublicSite && ready
        ? normalizePublicSiteLogoValue(resolvePublicSiteBrandLogoUrl(si))
        : null;
    let brandName =
      requiresPublicSite && ready ? resolvePublicSiteBrandDisplayName(si) : null;
    const brandDisplayType =
      requiresPublicSite && ready ? resolvePublicSiteBrandDisplayType(si) : null;

    // While the public-site identity is still loading (navigation / cold deep link),
    // fall back to the persisted agency logo so the loader never flashes the
    // HotelsRiver brand on an agent's public site.
    if (requiresPublicSite && !brandLogoUrl) {
      const persisted = readPersistedPublicSiteIdentity();
      const persistedLogo = normalizePublicSiteLogoValue(persisted?.metadata?.logo);
      if (persistedLogo) brandLogoUrl = persistedLogo;
      if (!brandName && persisted?.metadata?.agencyName) {
        brandName = persisted.metadata.agencyName;
      }
    }

    // True while a public site identity is still resolving (not yet ready/failed).
    const publicSitePending =
      requiresPublicSite &&
      !["ready", "error", "empty", "missing"].includes(String(siteIdentityStatus || ""));

    return {
      requiresPublicSite,
      siteIdentityStatus,
      brandLogoUrl,
      brandName,
      brandDisplayType,
      publicSitePending,
    };
  }, [ctx?.requiresPublicSite, ctx?.siteIdentityStatus, ctx?.siteIdentity]);
}

/**
 * SSOT ללוגו ה-header של הסייט הציבורי. סדר העדפה:
 *   1) prop מפורש (ה-public flow מעביר siteIdentity.logo) — ללא תקרת גודל perf
 *   2) זהות הסייט הציבורי (branding.logo_url → branding.logo → logo)
 *   3) לוגו הסוכן המחובר (דשבורד) מ-localStorage
 *   4) HotelsRiver fallback
 * אם brand_display_type === "agency_text" ואין לוגו — מחזיר שם סוכנות (kind="agency_text").
 *
 * @param {string|null} [publicSiteLogoProp]
 * @returns {{ kind: "logo"|"agency_text"|"fallback", src: string|null, text: string|null, source: string, brandDisplayType: string|null }}
 */
export function usePublicSiteHeaderBrand(publicSiteLogoProp = null) {
  const ctx = useOptionalPublicSite();
  const requiresPublicSite = Boolean(ctx?.requiresPublicSite);
  const siteIdentityStatus = ctx?.siteIdentityStatus ?? null;
  const siteIdentity = ctx?.siteIdentity ?? null;
  const siteId = ctx?.siteId ?? ctx?.resolvedSiteId ?? null;

  const brand = useMemo(() => {
    const ready = siteIdentityStatus === "ready";

    // 1) explicit prop (public flow) — brand header logo must not be dropped by a perf size cap
    const propLogo = normalizePublicSiteLogoValue(publicSiteLogoProp);
    if (propLogo) {
      return { kind: "logo", src: propLogo, text: null, source: "prop", brandDisplayType: "agent_logo" };
    }

    // 2) public site identity branding (branding.logo_url → branding.logo → logo)
    if (requiresPublicSite && ready) {
      const resolved = resolvePublicSiteHeaderLogo(siteIdentity, {
        fallbackLogoUrl: HOTELSRIVER_OFFICIAL_LOGO_URL,
      });
      if (resolved.kind === "logo" || resolved.kind === "agency_text") {
        return resolved;
      }
    }

    // 3) logged-in agent's own logo (agent dashboard usage)
    const userInfo = getUserInfo();
    const ownLogo = normalizeAgentImageValue(userInfo?.companylogobase65);
    if (ownLogo) {
      return { kind: "logo", src: ownLogo, text: null, source: "userInfo", brandDisplayType: "agent_logo" };
    }

    // 4) HotelsRiver fallback (only when there is no valid agent logo)
    return {
      kind: "fallback",
      src: HOTELSRIVER_OFFICIAL_LOGO_URL,
      text: null,
      source: "hotelsriver_fallback",
      brandDisplayType: "hotelsriver_fallback",
    };
  }, [publicSiteLogoProp, requiresPublicSite, siteIdentityStatus, siteIdentity]);

  // TEMP trace — verify logo source resolution per siteId (DEV only).
  useEffect(() => {
    if (!import.meta.env.DEV || typeof window === "undefined") return;
    const branding = siteIdentity?.branding || null;
    // eslint-disable-next-line no-console
    console.info(HEADER_LOGO_TRACE, {
      siteId: siteId || null,
      hasBrandingLogoUrl: Boolean(branding?.logo_url),
      hasBrandingLogo: Boolean(branding?.logo),
      hasTopLevelLogo: Boolean(siteIdentity?.logo),
      brandDisplayType: brand.brandDisplayType ?? null,
      resolvedLogoSource: brand.source,
    });
  }, [siteId, siteIdentity, brand.brandDisplayType, brand.source]);

  return brand;
}

/**
 * קישור לעמוד הבית של הסייט הציבורי (תוך שמירת ה-siteId), עבור לוגו קליקבילי ב-header.
 * מחזיר href ל-SSR/SEO/middle-click + onClick לניווט SPA נקי (ללא פרמטרים של חבילה/חיפוש).
 *
 * @returns {{ href: string, onClick: (e: import("react").MouseEvent) => void }}
 */
export function usePublicSiteHomeLink() {
  const navigate = useNavigate();
  const location = useLocation();

  const href = useMemo(
    () =>
      appendPublicSiteToken("/vacation-packages?category=home", null, {
        search: location.search,
      }),
    [location.search]
  );

  const onClick = useCallback(
    (event) => {
      if (!event) {
        navigate(href);
        return;
      }
      // Respect new-tab / modifier clicks — let the browser handle the href.
      if (
        event.defaultPrevented ||
        (event.button != null && event.button !== 0) ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      event.preventDefault();
      navigate(href);
    },
    [navigate, href]
  );

  return { href, onClick };
}
